/**
 * track() facade — the ONLY way the app emits analytics events (ADR-003).
 *
 * Enforcement pipeline (tech spec §4.2), applied in order:
 *   1. registry allow-list  → unknown prop keys dropped
 *   2. PHI key denylist     → matching keys dropped EVEN IF allow-listed
 *   3. value truncation     → string values capped at 128 chars
 *   4. reliability limiter   → 1 reliability event per key per 60s per session
 *   5. post-logout gate     → events after resetAnalytics() are dropped
 * Never throws into a caller. No-ops entirely when the client is null (env unset).
 */
import { getClient } from "./client";
import { devWarn } from "./devWarn";
import { isPhiKey, MAX_PROP_VALUE_LENGTH } from "./phi";
import { EVENT_DEFS } from "./registry";
import type { EventMap, EventName } from "./registry";

export { PHI_KEY_DENYLIST, MAX_PROP_VALUE_LENGTH } from "./phi";

/** Rate-limit window for reliability events (api errors / socket drops). */
export const RELIABILITY_WINDOW_MS = 60_000;

/** Dropped after resetAnalytics() until the next identifyUser() (US-A4). */
let suppressed = false;

/** key → last-emitted epoch ms, for the reliability rate limiter. */
const reliabilityWindows = new Map<string, number>();

/** System props stamped by the pipeline itself; kept regardless of the per-event allow-list. */
const RESERVED_SYSTEM_KEYS: ReadonlySet<string> = new Set(["flow_id"]);

/**
 * Layers 1–3 of the pipeline as a pure function (exported for direct testing).
 * `allowedPropsOverride` lets tests prove the denylist beats a bad allow-list.
 * Dropped keys emit a dev-only warning so silently-lost props are discoverable.
 */
export function sanitizeEventProps(
  event: EventName,
  props: Record<string, unknown>,
  allowedPropsOverride?: ReadonlyArray<string>
): Record<string, string | number | boolean> {
  const allowed = new Set<string>(
    allowedPropsOverride ?? EVENT_DEFS[event].allowedProps
  );
  const clean: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(props)) {
    if (value === null || value === undefined) {
      continue; // absent values are not a mistake — drop quietly
    }
    if (isPhiKey(key)) {
      devWarn(`dropped PHI-denylisted key '${key}' from event '${event}'`);
      continue; // layer 2: denylist wins even over the allow-list
    }
    if (!allowed.has(key) && !RESERVED_SYSTEM_KEYS.has(key)) {
      devWarn(`dropped unregistered key '${key}' from event '${event}'`);
      continue; // layer 1: allow-list
    }
    if (typeof value === "string") {
      clean[key] = value.slice(0, MAX_PROP_VALUE_LENGTH); // layer 3: truncation
    } else if (typeof value === "number" || typeof value === "boolean") {
      clean[key] = value;
    } else {
      devWarn(
        `dropped non-primitive value for key '${key}' on event '${event}'`
      );
    }
  }
  return clean;
}

/**
 * Emit a registered analytics event. Fail-open: silently no-ops when analytics
 * is disabled or suppressed, and never throws into the caller.
 */
export function track<E extends EventName>(event: E, props: EventMap[E]): void {
  try {
    if (suppressed) {
      return;
    }
    const client = getClient();
    if (client === null) {
      return;
    }
    const clean = sanitizeEventProps(
      event,
      props as unknown as Record<string, unknown>
    );
    clean.feature_area = EVENT_DEFS[event].featureArea;
    client.track(event, clean);
  } catch (error) {
    devWarn(`track('${event}') failed`, error);
  }
}

/** True (and records the timestamp) only when the key is outside its window. */
function isRateLimited(key: string): boolean {
  const now = Date.now();
  const last = reliabilityWindows.get(key);
  if (last !== undefined && now - last < RELIABILITY_WINDOW_MS) {
    return true;
  }
  reliabilityWindows.set(key, now);
  return false;
}

/** Axios-interceptor helper — rate-limited per (endpoint_group, status_code). */
export function trackApiError(input: {
  endpointGroup: string;
  statusCode: number;
  method: string;
}): void {
  if (isRateLimited(`api:${input.endpointGroup}:${input.statusCode}`)) {
    return;
  }
  track("api_error_encountered", {
    endpoint_group: input.endpointGroup,
    status_code: input.statusCode,
    method: input.method,
  });
}

/** Socket-service helper — rate-limited to 1 per 60s per session. */
export function trackSocketDisconnected(input: {
  reason?: string;
  reconnectAttempts?: number;
  sessionAgeMs?: number;
}): void {
  if (isRateLimited("socket")) {
    return;
  }
  track("socket_disconnected", {
    reason: input.reason,
    reconnect_attempts: input.reconnectAttempts,
    session_age_ms: input.sessionAgeMs,
  });
}

/** Flipped by identity.ts: identified=true resumes sending, false suppresses (post-logout). */
export function __setIdentifiedForInternalUse(identified: boolean): void {
  suppressed = !identified;
}

/** Test-only: clears the rate-limiter windows and the suppression gate. */
export function __resetTrackStateForTests(): void {
  suppressed = false;
  reliabilityWindows.clear();
}
